import { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, CheckCircle2, CircleDot, Circle, Lightbulb, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { extractSignals, scoreFromSignals, type CitabilityFactor } from '@/utils/citability';

interface GeoPanelProps {
  /** Editor content — Markdown (posts) or HTML (pages); the scorer detects which. */
  content: string;
  /** TL;DR source. `undefined` when the content type has no excerpt (pages). */
  excerpt?: string;
  /** `undefined` when the content type has no author field (pages). */
  hasAuthor?: boolean;
  /** `undefined` when the content type has no publish date (pages). */
  hasDate?: boolean;
}

/** Debounce a value so scoring doesn't run on every keystroke. */
function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

function band(score: number): {
  label: string;
  text: string;
  badge: 'success' | 'warning' | 'destructive';
} {
  if (score >= 70) return { label: 'Strong', text: 'text-success', badge: 'success' };
  if (score >= 50) return { label: 'Needs work', text: 'text-primary', badge: 'warning' };
  return { label: 'Weak', text: 'text-destructive', badge: 'destructive' };
}

function ScoreRing({ score, colorClass }: { score: number; colorClass: string }) {
  const r = 26;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - Math.min(100, Math.max(0, score)) / 100);
  return (
    <div className="relative h-16 w-16 shrink-0">
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" strokeWidth="6" className="stroke-muted" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          strokeWidth="6"
          strokeLinecap="round"
          stroke="currentColor"
          className={cn('geo-score-arc', colorClass)}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 300ms var(--ease-out)' }}
        />
      </svg>
      <div
        className={cn('absolute inset-0 flex items-center justify-center text-lg font-semibold', colorClass)}
      >
        {score}
      </div>
    </div>
  );
}

function FactorRow({ factor }: { factor: CitabilityFactor }) {
  const Icon =
    factor.status === 'met' ? CheckCircle2 : factor.status === 'partial' ? CircleDot : Circle;
  const iconColor =
    factor.status === 'met'
      ? 'text-success'
      : factor.status === 'partial'
        ? 'text-primary'
        : 'text-muted-foreground';
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 text-sm">
        <Icon className={cn('h-4 w-4 shrink-0', iconColor)} />
        <span className="font-medium">{factor.label}</span>
        <span className="text-xs text-muted-foreground">{factor.detail}</span>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {factor.points}/{factor.weight}
        </span>
      </div>
      {factor.suggestion && (
        <div className="flex gap-2 pl-6 text-xs text-muted-foreground">
          <Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <span>{factor.suggestion}</span>
        </div>
      )}
    </div>
  );
}

/**
 * GEO citability panel (AB#243-B). Shows a live 0-100 score, a per-factor
 * breakdown with fix-it suggestions on weak factors, and non-blocking hints for
 * the top missing GEO elements (TL;DR / author / date). Collapsible, mirroring
 * PostSeoPanel; the score badge stays visible in the header when collapsed.
 * Reused by the post and page editors — pass `undefined` for fields a content
 * type lacks and they degrade out of the score and hints.
 */
export function GeoPanel({ content, excerpt, hasAuthor, hasDate }: GeoPanelProps) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const autoOpened = useRef(false);

  const debounced = useDebounced(content, 400);
  const { signals, result } = useMemo(() => {
    const signals = extractSignals(debounced);
    return { signals, result: scoreFromSignals(signals, { hasAuthor, hasDate }) };
  }, [debounced, hasAuthor, hasDate]);
  const b = band(result.score);

  // Auto-expand once when the score needs attention (mirrors PostSeoPanel
  // revealing existing metadata on load). Never blocks; user can re-collapse.
  // Gated on real words so a pristine/new post — empty content, or a blank
  // editor doc like `<p></p>`, both 0 words, score 0 — never force-opens on
  // mount; only after actual content exists or the user types (AB#421).
  useEffect(() => {
    if (!autoOpened.current && signals.words > 0 && result.score < 70) {
      setOpen(true);
      autoOpened.current = true;
    }
  }, [signals.words, result.score]);

  const hints = useMemo(() => {
    const out: Array<{ key: string; text: string }> = [];
    if (excerpt !== undefined && excerpt.trim() === '') {
      out.push({ key: 'tldr', text: 'Add a TL;DR (excerpt) so AI answers have a crisp summary to quote.' });
    }
    if (hasAuthor === false) {
      out.push({ key: 'author', text: 'No author set — a byline boosts AI attribution.' });
    }
    if (hasDate === false) {
      out.push({ key: 'date', text: 'No publish date — freshness helps AI search.' });
    }
    return out.filter((h) => !dismissed.has(h.key));
  }, [excerpt, hasAuthor, hasDate, dismissed]);

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 text-sm font-medium hover:text-foreground/80"
      >
        <ChevronDown className={`h-4 w-4 transition-transform ease-out ${open ? 'rotate-180' : ''}`} />
        GEO citability
        <Badge variant={b.badge} className="ml-auto tabular-nums">
          {result.score}
        </Badge>
      </button>

      {/* Non-blocking hints — visible even when collapsed, never gate typing/saving. */}
      {hints.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {hints.map((h) => (
            <Badge key={h.key} variant="warning" className="gap-1.5 py-1 font-normal">
              <Lightbulb className="h-3 w-3 shrink-0" />
              {h.text}
              <button
                type="button"
                onClick={() => setDismissed((d) => new Set(d).add(h.key))}
                className="ml-0.5 rounded hover:text-foreground"
                aria-label="Dismiss hint"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {open && (
        <div className="disclosure-reveal space-y-4 border-l-2 border-muted pl-6">
          <div className="flex items-center gap-4">
            <ScoreRing score={result.score} colorClass={b.text} />
            <div>
              <div className="font-medium">{b.label}</div>
              <p className="text-xs text-muted-foreground">
                How likely AI search is to cite this page, from its structure and signals.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {result.factors.map((factor) => (
              <FactorRow key={factor.key} factor={factor} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
